import React from 'react';

import { routes } from '@aline/routes';
import { parsePath, Route, Routes } from 'react-router-dom';
import { MainPageContent } from 'src/components/pages/MainPage';
import { Editor } from 'src/editor/Editor';
import { createRelativeRouter } from 'src/helpers/router-helpers';

import { getIsFocusableRoute, useIsExternalSession } from '../focused-view';
import { NotPermittedPage } from '../NotPermittedPage';

const { toRelativePath: toRelativeHomePath } = createRelativeRouter(routes.Home, {
  nestedRoutes: true,
});

interface TabContentRoutesProps {
  location: string;
}

/**
 * Route definitions for tab content. Memoized to prevent re-renders when the
 * parent re-renders but location hasn't changed.
 *
 * Parses `location` into a full Location object so search + hash reach
 * descendant `useLocation()` consumers (e.g. `useWalkthroughActive` reads
 * `?walkThrough=true`). Passing the raw string treats the entire value as
 * pathname and drops the query.
 */
export const TabContentRoutes = React.memo(
  function TabContentRoutes({ location }: TabContentRoutesProps): JSX.Element {
    const isExternalSession = useIsExternalSession();
    if (isExternalSession && !getIsFocusableRoute(location)) return <NotPermittedPage />;

    const parsed = parsePath(location);
    return (
      <Routes
        location={{
          hash: parsed.hash ?? '',
          key: 'default',
          pathname: parsed.pathname ?? '/',
          search: parsed.search ?? '',
          state: null,
        }}
      >
        <Route
          element={<Editor />}
          path={toRelativeHomePath(routes.DocumentDetailWorkflowDetail)}
        />
        <Route element={<Editor />} path={toRelativeHomePath(routes.DocumentDetail)} />
        <Route element={<MainPageContent />} path="*" />
      </Routes>
    );
  },
  (prev, next) => prev.location === next.location
);
